import React from "react";
import { inputStyle, thStyle, tdStyle } from "../../../components/ui";
import AccountPicker from "./AccountPicker";
import { fmtMoney } from "./glContext";
import { linkBtn, numTh } from "./glUi";

// The debit / credit lines of a journal (or a recurring journal). A line is
// { account: { QUALIFIER: value }, description, entered_dr, entered_cr }.
export const blankLine = () => ({ account: null, description: "", entered_dr: "", entered_cr: "" });

// Totals in whole minor units so 0.1 + 0.2 adds up exactly.
export function lineTotals(lines, digits = 2) {
  const f = 10 ** digits;
  const sum = (k) => lines.reduce((s, l) => s + Math.round((Number(l[k]) || 0) * f), 0);
  const dr = sum("entered_dr");
  const cr = sum("entered_cr");
  return { dr: dr / f, cr: cr / f, diff: (dr - cr) / f };
}

// What the API takes: blank lines are dropped, amounts are numbers.
export const linesForApi = (lines) =>
  lines
    .filter((l) => l.account || Number(l.entered_dr) || Number(l.entered_cr))
    .map((l) => ({ account: l.account, description: l.description || undefined, entered_dr: Number(l.entered_dr) || 0, entered_cr: Number(l.entered_cr) || 0 }));

const amountInput = { ...inputStyle, height: 38, textAlign: "right", fontVariantNumeric: "tabular-nums" };

export default function LinesEditor({ lines, onChange, structure, companyValue, currency, digits = 2, disabled }) {
  const set = (i, patch) => onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const t = lineTotals(lines, digits);
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="table mb-2" style={{ minWidth: 760 }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: 36 }}>#</th>
            <th style={{ ...thStyle, width: "38%" }}>Account</th>
            <th style={thStyle}>Description</th>
            <th style={{ ...numTh, width: 130 }}>Debit</th>
            <th style={{ ...numTh, width: 130 }}>Credit</th>
            <th style={{ ...thStyle, width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td style={{ ...tdStyle, verticalAlign: "top", paddingTop: 20 }}>{i + 1}</td>
              <td style={{ ...tdStyle, verticalAlign: "top" }}>
                <AccountPicker id={`je-acct-${i}`} structure={structure} companyValue={companyValue} value={l.account} onChange={(account) => set(i, { account })} disabled={disabled} />
              </td>
              <td style={{ ...tdStyle, verticalAlign: "top" }}>
                <input className="form-control" style={{ ...inputStyle, height: 38 }} aria-label={`Line ${i + 1} description`} maxLength={240} value={l.description || ""} disabled={disabled} onChange={(e) => set(i, { description: e.target.value })} />
              </td>
              <td style={{ ...tdStyle, verticalAlign: "top" }}>
                <input
                  type="number"
                  min={0}
                  step="any"
                  className="form-control"
                  style={amountInput}
                  aria-label={`Line ${i + 1} debit`}
                  value={l.entered_dr}
                  disabled={disabled}
                  onChange={(e) => set(i, { entered_dr: e.target.value, entered_cr: e.target.value ? "" : l.entered_cr })}
                />
              </td>
              <td style={{ ...tdStyle, verticalAlign: "top" }}>
                <input
                  type="number"
                  min={0}
                  step="any"
                  className="form-control"
                  style={amountInput}
                  aria-label={`Line ${i + 1} credit`}
                  value={l.entered_cr}
                  disabled={disabled}
                  onChange={(e) => set(i, { entered_cr: e.target.value, entered_dr: e.target.value ? "" : l.entered_dr })}
                />
              </td>
              <td style={{ ...tdStyle, verticalAlign: "top", paddingTop: 18 }}>
                {!disabled && lines.length > 2 ? (
                  <button type="button" style={linkBtn} aria-label={`Remove line ${i + 1}`} onClick={() => onChange(lines.filter((_, j) => j !== i))}>
                    ✕
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td style={tdStyle} colSpan={3}>
              {!disabled ? (
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => onChange([...lines, blankLine()])}>
                  Add line
                </button>
              ) : null}
            </td>
            <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>{fmtMoney(t.dr, digits)}</td>
            <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>{fmtMoney(t.cr, digits)}</td>
            <td style={tdStyle} />
          </tr>
          <tr>
            <td colSpan={6} style={{ ...tdStyle, borderBottom: "none", textAlign: "right", fontSize: 13, fontWeight: 600, color: t.diff === 0 ? "#065F46" : "#991B1B" }}>
              {t.diff === 0 ? `Balanced in ${currency}` : `Out of balance by ${fmtMoney(Math.abs(t.diff), digits)} ${currency}`}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
